/**
 * useEdgePathCache — assigns SVG pathD to each edge for the main flowchart.
 *
 * - Uses ELK `pathD` as-is when both endpoints match ELK `autoPositions` within `layoutEpsilon`.
 * - When an endpoint diverges from auto (manual placement, drag in progress, transient
 *   persist/measure drift), the ELK path is *warped*: each end is translated by its own
 *   node's delta so the edge stays anchored to the real card while keeping the package's
 *   orthogonal routing style. ELK itself cannot route edges around pinned nodes, so this
 *   synchronous warp is what keeps manual layouts off the legacy router.
 * - Any ELK/warped path is validated against cards at their LIVE positions once the board
 *   has displaced nodes: ELK only guarantees clearance for the auto layout, so a moved
 *   card can sit on top of a path. Blocked edges fall back to the obstacle-avoiding
 *   `getEdgePath` router for that edge only.
 * - `getEdgePath` is also the fallback for milestone edges, edges with no ELK path, or
 *   when a warp is not possible.
 */

import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { getEdgePath } from '../utils/edgePaths';
import { warpPathToNodeDeltas, pathIntersectsAnyNodeRect } from '../layout/elkEdgePathAdapter';
import { buildObstacleSpatialIndex, OBSTACLE_SPATIAL_INDEX_MIN_NODES } from '../utils/pathHelpers';
import { digestNodePositionsRecord } from '../utils/positionDigest';

const DEFAULT_LAYOUT_EPS = 1.5;
/** Clearance around cards when validating ELK/warped paths against live positions. */
const LIVE_OBSTACLE_PAD = 4;

function positionMatchesAuto(nodeId, nodePositions, autoPositions, eps) {
  // Missing layout data must not opt into ELK geometry (would keep stale pathD).
  if (!nodePositions || !autoPositions) return false;
  const n = nodePositions[nodeId];
  const a = autoPositions[nodeId];
  if (!n || !Number.isFinite(n.x) || !Number.isFinite(n.y)) return false;
  if (!a || !Number.isFinite(a.x) || !Number.isFinite(a.y)) return false;
  return Math.abs(n.x - a.x) <= eps && Math.abs(n.y - a.y) <= eps;
}

function bothEndpointsMatchElk(edge, nodePositions, autoPositions, eps) {
  return (
    positionMatchesAuto(edge.from, nodePositions, autoPositions, eps) &&
    positionMatchesAuto(edge.to, nodePositions, autoPositions, eps)
  );
}

function nodeDelta(nodeId, nodePositions, autoPositions) {
  if (!nodePositions || !autoPositions) return null;
  const n = nodePositions[nodeId];
  const a = autoPositions[nodeId];
  if (!n || !Number.isFinite(n.x) || !Number.isFinite(n.y)) return null;
  if (!a || !Number.isFinite(a.x) || !Number.isFinite(a.y)) return null;
  return { x: n.x - a.x, y: n.y - a.y };
}

function computeEdgePath(edge, sharedSpatialIndex) {
  return getEdgePath(
    edge.fromPos,
    edge.toPos,
    edge.fromDims,
    edge.toDims,
    edge.allNodes || [],
    edge.edgeIndex,
    null,
    edge.id,
    edge.fromOffset || 0,
    edge.toOffset || 0,
    edge.fromSide,
    edge.toSide,
    null,
    edge.from,
    edge.to,
    sharedSpatialIndex,
  );
}

/**
 * ELK path for this edge at the live endpoint positions: base path when the endpoints sit
 * on the auto layout, warped path when they diverge. Null → caller uses the legacy router.
 */
function resolveElkPathForEdge(edge, elkMap, np, ap, layoutEpsilon) {
  if (!elkMap || edge.isMilestoneEdge) return null;
  const baseD = elkMap[edge.id];
  if (!baseD) return null;
  if (bothEndpointsMatchElk(edge, np, ap, layoutEpsilon)) return baseD;
  const ds = nodeDelta(edge.from, np, ap);
  const dt = nodeDelta(edge.to, np, ap);
  if (!ds || !dt) return null;
  return warpPathToNodeDeltas(baseD, ds, dt);
}

/** True when any node's live position diverges from ELK auto (manual layout / mid-drag). */
function anyNodeDisplaced(nodePositions, autoPositions, eps) {
  if (!nodePositions || !autoPositions) return false;
  for (const id of Object.keys(nodePositions)) {
    const n = nodePositions[id];
    const a = autoPositions[id];
    if (!n || !a || !Number.isFinite(n.x) || !Number.isFinite(a.x)) continue;
    if (Math.abs(n.x - a.x) > eps || Math.abs(n.y - a.y) > eps) return true;
  }
  return false;
}

/**
 * @param {Array} edges — from generateEdges (includes fromPos, toPos, sides, dims, …)
 * @param {Record<string,string>|null|undefined} precomputedPathByEdgeId — ELK edge id → path d
 * @param {boolean|Object} [pathOptions] — legacy: `true` meant "skip all ELK" (avoid passing true).
 *   Prefer `{ nodePositions, autoPositions, useElkPathsDespiteEndpointMismatch?,
 *   suppressEdgesUntilElkReady? }`.
 */
export function useEdgePathCache(
  edges,
  precomputedPathByEdgeId = null,
  pathOptions = null,
) {
  const {
    skipElkPaths = false,
    nodePositions = null,
    autoPositions = null,
    layoutEpsilon = DEFAULT_LAYOUT_EPS,
    useElkPathsDespiteEndpointMismatch = false,
    suppressEdgesUntilElkReady = false,
  } = typeof pathOptions === 'boolean'
    ? { skipElkPaths: pathOptions }
    : pathOptions || {};

  const nodePositionsRef = useRef(nodePositions);
  const autoPositionsRef = useRef(autoPositions);
  nodePositionsRef.current = nodePositions;
  autoPositionsRef.current = autoPositions;

  const [endpointDigestTick, setEndpointDigestTick] = useState(0);
  const lastEndpointDigestRef = useRef(null);

  useLayoutEffect(() => {
    const d = `${digestNodePositionsRecord(nodePositionsRef.current)}@${digestNodePositionsRecord(autoPositionsRef.current)}`;
    if (lastEndpointDigestRef.current === null) {
      lastEndpointDigestRef.current = d;
      return;
    }
    if (d !== lastEndpointDigestRef.current) {
      lastEndpointDigestRef.current = d;
      setEndpointDigestTick((n) => n + 1);
    }
  }, [nodePositions, autoPositions]);

  return useMemo(() => {
    if (!edges?.length) return [];

    const np = nodePositionsRef.current;
    const ap = autoPositionsRef.current;

    const elkMap = precomputedPathByEdgeId && Object.keys(precomputedPathByEdgeId).length > 0
      ? precomputedPathByEdgeId
      : null;

    // Once any card sits away from its auto position, ELK's clearance guarantee no longer
    // holds for the live board — validate ELK/warped paths against live card rects.
    // Unwarped base paths can only newly collide with displaced cards; warped paths moved
    // themselves, so they are checked against every card.
    const validateAgainstLiveCards =
      !useElkPathsDespiteEndpointMismatch && anyNodeDisplaced(np, ap, layoutEpsilon);
    const allNodesShared = edges[0]?.allNodes || [];
    const displacedNodes = validateAgainstLiveCards
      ? allNodesShared.filter(
          (node) => !positionMatchesAuto(node.id, np, ap, layoutEpsilon),
        )
      : [];

    const elkPathByEdgeId = new Map();
    let needsComputedPaths = suppressEdgesUntilElkReady;
    if (!skipElkPaths && elkMap && !suppressEdgesUntilElkReady) {
      for (const edge of edges) {
        let resolved = useElkPathsDespiteEndpointMismatch
          ? (!edge.isMilestoneEdge && elkMap[edge.id]) || null
          : resolveElkPathForEdge(edge, elkMap, np, ap, layoutEpsilon);
        if (resolved && validateAgainstLiveCards) {
          const isWarped = !bothEndpointsMatchElk(edge, np, ap, layoutEpsilon);
          const obstacles = isWarped ? edge.allNodes || [] : displacedNodes;
          if (
            pathIntersectsAnyNodeRect(
              resolved,
              obstacles,
              new Set([edge.from, edge.to]),
              LIVE_OBSTACLE_PAD,
            )
          ) {
            resolved = null;
          }
        }
        if (resolved) {
          elkPathByEdgeId.set(edge.id, resolved);
        } else {
          needsComputedPaths = true;
        }
      }
    } else {
      needsComputedPaths = true;
    }

    const sharedSpatialIndex =
      needsComputedPaths &&
      edges[0]?.allNodes &&
      edges[0].allNodes.length >= OBSTACLE_SPATIAL_INDEX_MIN_NODES
        ? buildObstacleSpatialIndex(edges[0].allNodes)
        : null;

    return edges.map((edge) => {
      if (!suppressEdgesUntilElkReady) {
        const elkD = elkPathByEdgeId.get(edge.id);
        if (elkD) return { ...edge, pathD: elkD };
      }
      return { ...edge, pathD: computeEdgePath(edge, sharedSpatialIndex) };
    });
  }, [
    edges,
    precomputedPathByEdgeId,
    skipElkPaths,
    endpointDigestTick,
    layoutEpsilon,
    useElkPathsDespiteEndpointMismatch,
    suppressEdgesUntilElkReady,
  ]);
}

export default useEdgePathCache;
